import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { ExportToCsv, Options } from "export-to-csv";
import * as moment from "moment";
import { EventService } from "src/app/services/event.service";
import Swal from "sweetalert2";
import { PdiMetadataDialougeComponent } from "../metadata-dialouge/metadata-dialouge.component";

interface DashboardTableData {
  "S.No": number;
  "Test ID": string;
  Date: string;
  "Battery ID": string;
  "BMS ID": string;
  "Test Result": string;
  Analytics: string;
  Download: string;
}

@Component({
  selector: "app-pdi-lite-dashboard-table",
  templateUrl: "./pdi-lite-dashboard-table.component.html",
  styleUrls: ["./pdi-lite-dashboard-table.component.css"],
})
export class PdiLiteDashboardTableComponent implements OnInit {
  constructor(
    private eventService: EventService,
    private dialogue: MatDialog
  ) {}

  @Input() packId: string;
  @ViewChild("tableFilterInput") fitlerInput: ElementRef<HTMLInputElement>;
  @ViewChild("pdiLitetableFilterInput")
  pdiLitetableFilterInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild("pdiLitePaginator") pdiLitePaginator: MatPaginator;
  dashboardTableCols: string[] = [
    "S.No",
    "Test ID",
    "Battery ID",
    "BMS ID",
    "Date",
    "Test Result",
    "Analytics",
    "Download",
  ];

  pdiLiteDashboardDataSource: MatTableDataSource<DashboardTableData> =
    new MatTableDataSource<DashboardTableData>([]);

  ngOnInit(): void {
    this.getPdiLiteDashboardTableData();
  }

  getPdiLiteDashboardTableData() {
    this.eventService.getPdiLiteDashboardTableData("tg").subscribe({
      next: (res) => {
        if (res.ok) {
          const tableData = res.body.data.map((_, i) => {
            return {
              "S.No": i + 1,
              "Test ID": _.test_id,
              Date: moment(new Date(_.date)).format("YYYY-MM-DD"),
              "Battery ID": _.pack_id,
              "BMS ID": _.bms_id,
              "Test Result": _.test_result,
              Analytics: "Dialogue",
              Download: "PDF",
            };
          });

          if (this.packId) {
            this.pdiLiteDashboardDataSource.data = tableData
              .filter((row) => row["Battery ID"] === this.packId)
              .map((_, i) => ({ ..._, "S.No": i + 1 })); // Re-indexing S.No based on filtered data
          } else {
            this.pdiLiteDashboardDataSource.data = tableData;
          }
        }
      },
    });
  }

  ngAfterViewInit(): void {
    this.pdiLiteDashboardDataSource.paginator = this.pdiLitePaginator;
    document.querySelectorAll(".mat-tab-body-content").forEach((_) => {
      (_ as HTMLDivElement).style.overflow = "visible";
    });
  }

  downloadCsvReport() {
    const testIds = this.pdiLiteDashboardDataSource.filteredData.map(
      (_) => _["Test ID"]
    );

    if (testIds.length === 0) {
      Swal.fire({
        title: "No Test Data Found",
        text: "At lest one test data should be present to download report",
        icon: "warning",
      });
      return;
    }

    this.eventService.getPdiLiteCsvReportData("tg").subscribe({
      next: (res) => {
        if (res.status === 200 && res.body.success === true) {
          const validData = res.body.data?.filter((item) =>
            testIds.includes(item.test_id)
          );

          const exportOptions: Options = {
            useKeysAsHeaders: true,
            useTextFile: false,
            filename: `pdi_report_tg${moment(new Date()).format("YYYY-MM-DD")}`,
          };

          const csvExporter = new ExportToCsv(exportOptions);
          csvExporter.generateCsv(validData);
        }
      },
    });
  }

  applyPdiLiteTableFilter() {
    const key = this.pdiLitetableFilterInput.nativeElement.value;
    this.pdiLiteDashboardDataSource.filter = key.trim().toLowerCase();
  }

  generateTestResultStyle(result: string) {
    let style = {
      textTransform: "capitalize",
    };

    if (result === "pass" || result === "Pass") {
      style["color"] = "green";
    } else if (result === "fail" || result === "Fail") {
      style["color"] = "red";
    }

    return style;
  }

  openPdiLiteDialog(rowData: DashboardTableData) {
    const testId = rowData["Test ID"];

    this.eventService.getPdiLiteDialougeData(testId).subscribe({
      next: (res) => {
        if (res.body.success) {
          const data = res.body.data;
          const battery_type = res.body.battery_type;

          const test_id = data["test_id"];
          const date = moment(new Date(data["created_at"])).format(
            "DD-MM-YYYY"
          );

          const pack_data = {
            pack_id: data["pack_id"],
            bms_id: data["bms_id"],
            product_id: data["product_id"],
            variant_id: data["variant_id"],
            test_type: data["test_type"],
            hardware_version: data["bms_hw_ver"],
          };

          const metaData = { ...data };

          delete metaData["created_at"];
          delete metaData["test_id"];

          Object.keys(pack_data).forEach((key) => {
            delete metaData[key];
          });

          this.dialogue.open(PdiMetadataDialougeComponent, {
            height: "95%",
            width: "90%",
            maxWidth: "1250px",
            autoFocus: true,
            data: {
              test_id,
              date,
              pack_data,
              metaData,
              battery_type,
            },
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Something went wrong while fetching test meta data",
          });
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  generateReportPdf(rowData: DashboardTableData) {
    console.log("pdf report generating", rowData);
    // console.log(rowData);
  }

  downloadPDILiteTestPdfReport(testId: string) {
    this.eventService
      .downloadPDILiteTestPdfReport(testId)
      .subscribe((res: { success: boolean; data: string; message: string }) => {
        if (res.success) {
          if (res.data) {
            // opens direct download link
            window.location.href = res.data;
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: res.message ?? "",
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res.message ?? "",
          });
        }
      });
  }
}
